import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Trash2,
  User,
  Users,
  ShieldCheck,
  Wallet,
  Check,
  Lock,
  Loader2,
} from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";

const STEPS = [
  { id: 1, label: "Family", Icon: User },
  { id: 2, label: "Students", Icon: Users },
  { id: 3, label: "Agreement", Icon: ShieldCheck },
  { id: 4, label: "Payment", Icon: Wallet },
];

const GRADES = [
  "Pre-K",
  "Kindergarten",
  "1st Grade",
  "2nd Grade",
  "3rd Grade",
  "4th Grade",
  "5th Grade",
  "6th Grade",
  "7th Grade",
  "8th Grade",
  "9th Grade",
  "10th Grade",
  "11th Grade",
  "12th Grade",
];

const emptyStudent = () => ({
  full_name: "",
  date_of_birth: "",
  grade_level: "",
  gender: "",
});

export default function EnrollPage() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [settings, setSettings] = useState(null);

  const [form, setForm] = useState({
    parent_first_name: "",
    parent_last_name: "",
    email: "",
    phone: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "AL",
    zip_code: "",
    password: "",
    password_confirm: "",
    students: [emptyStudent()],
    agreement_accepted: false,
    signature_name: "",
    payment_method: "",
  });

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => setSettings({}));
  }, []);

  if (settings === null) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <SiteHeader />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="animate-spin text-purple" size={30} />
        </div>
        <SiteFooter />
      </div>
    );
  }

  if (settings.enrollment_open === false) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <SiteHeader />
        <section className="flex-1 max-w-2xl w-full mx-auto px-5 py-24 text-center">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-gold text-ink mb-8">
            <Lock size={26} />
          </div>
          <div className="text-xs tracking-[0.22em] uppercase text-purple-light font-bold mb-4">
            Enrollment is currently closed
          </div>
          <h1 className="h-serif text-4xl sm:text-5xl text-purple-dark tracking-tight mb-6">
            We&apos;re not accepting applications right now.
          </h1>
          <p className="text-base text-ink/80 leading-relaxed mb-8 max-w-lg mx-auto">
            Enrollment for Zion Christian Academy is temporarily paused while
            we prepare for the upcoming school year. Please check back soon,
            or reach out directly and we&apos;ll let you know the moment it
            reopens.
          </p>
          <div className="grid sm:grid-cols-2 gap-3 max-w-md mx-auto mb-8">
            <a
              href={`mailto:${settings?.contact_email || "zca.k.12.26@outlook.com"}`}
              className="bg-purple text-white hover:bg-purple-dark font-bold px-5 py-3 rounded-sm text-sm"
              data-testid="closed-email-btn"
            >
              Email the school
            </a>
            <a
              href={`tel:${(settings?.contact_phone || "(205) 200-6257").replace(/[^\d+]/g, "")}`}
              className="border border-purple text-purple hover:bg-grey-light font-bold px-5 py-3 rounded-sm text-sm"
              data-testid="closed-phone-btn"
            >
              {settings?.contact_phone || "(205) 200-6257"}
            </a>
          </div>
          <Link
            to="/"
            className="text-sm text-purple hover:text-purple-dark link-underline"
            data-testid="closed-home-link"
          >
            ← Return home
          </Link>
        </section>
        <SiteFooter />
      </div>
    );
  }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setStudent = (i, k, v) =>
    setForm((f) => {
      const students = [...f.students];
      students[i] = { ...students[i], [k]: v };
      return { ...f, students };
    });

  const addStudent = () =>
    setForm((f) => ({ ...f, students: [...f.students, emptyStudent()] }));

  const removeStudent = (i) =>
    setForm((f) => ({ ...f, students: f.students.filter((_, idx) => idx !== i) }));

  const validateStep = () => {
    if (step === 1) {
      const req = [
        "parent_first_name",
        "parent_last_name",
        "email",
        "phone",
        "address_line1",
        "city",
        "state",
        "zip_code",
        "password",
      ];
      for (const k of req) {
        if (!String(form[k] || "").trim()) {
          toast.error(`Please fill in ${k.replace(/_/g, " ")}.`);
          return false;
        }
      }
      if (!/^\S+@\S+\.\S+$/.test(form.email)) {
        toast.error("Please enter a valid email.");
        return false;
      }
      if (form.password.length < 8) {
        toast.error("Password must be at least 8 characters.");
        return false;
      }
      if (form.password !== form.password_confirm) {
        toast.error("Passwords do not match.");
        return false;
      }
      return true;
    }
    if (step === 2) {
      if (form.students.length === 0) {
        toast.error("Please add at least one student.");
        return false;
      }
      for (let i = 0; i < form.students.length; i++) {
        const s = form.students[i];
        if (!s.full_name || !s.date_of_birth || !s.grade_level || !s.gender) {
          toast.error(`Please complete all fields for Student ${i + 1}.`);
          return false;
        }
      }
      return true;
    }
    if (step === 3) {
      if (!form.agreement_accepted) {
        toast.error("Please accept the church school agreement.");
        return false;
      }
      if (!form.signature_name.trim()) {
        toast.error("Please type your full name as your signature.");
        return false;
      }
      return true;
    }
    return true;
  };

  const next = () => {
    if (validateStep()) setStep((s) => Math.min(4, s + 1));
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    if (!form.payment_method) {
      toast.error("Please choose a payment option.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        parent_first_name: form.parent_first_name,
        parent_last_name: form.parent_last_name,
        email: form.email,
        phone: form.phone,
        address_line1: form.address_line1,
        address_line2: form.address_line2,
        city: form.city,
        state: form.state,
        zip_code: form.zip_code,
        password: form.password,
        students: form.students,
        payment_method: form.payment_method,
        agreement_accepted: form.agreement_accepted,
        signature_name: form.signature_name,
      };
      const { data } = await api.post("/enroll", payload);
      toast.success(`Application received (ID: ${data.enrollment_id})`);
      nav(`/enroll/confirmation/${data.enrollment_id}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const total = (settings?.enrollment_fee_per_child || 1500) * form.students.length;

  return (
    <div className="min-h-screen bg-white">
      <SiteHeader />

      {/* Banner */}
      <div className="bg-purple-dark text-white">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-12 text-center">
          <div className="text-xs tracking-[0.22em] uppercase text-gold-light font-bold mb-3">
            Enrollment · 2026–2027 school year
          </div>
          <h1 className="h-serif text-4xl sm:text-5xl tracking-tight mb-3">
            Welcome your family to Zion.
          </h1>
          <p className="text-white/80 text-base leading-relaxed max-w-xl mx-auto">
            Four short steps. Our director will personally review your
            application and welcome your family — usually within 1–2 business
            days.
          </p>
        </div>
      </div>

      {/* Progress */}
      <div className="border-b border-grey bg-grey-light">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-6">
          <div className="grid grid-cols-4 gap-2" data-testid="enrollment-progress">
            {STEPS.map((s) => {
              const done = s.id < step;
              const active = s.id === step;
              return (
                <div key={s.id} className="flex flex-col items-center" data-testid={`step-indicator-${s.id}`}>
                  <div
                    className={`h-1 w-full mb-3 ${
                      done || active ? "bg-gold" : "bg-grey"
                    }`}
                  />
                  <div
                    className={`flex items-center gap-2 text-xs font-bold uppercase tracking-widest ${
                      active ? "text-purple-dark" : done ? "text-purple" : "text-ink/50"
                    }`}
                  >
                    {done ? (
                      <Check size={14} className="text-gold" />
                    ) : (
                      <s.Icon size={14} />
                    )}
                    <span className="hidden sm:inline">{s.label}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-16">
        <div className="border border-grey bg-white p-6 sm:p-10">
          {step === 1 && (
            <StepFamily form={form} set={set} />
          )}
          {step === 2 && (
            <StepStudents
              students={form.students}
              setStudent={setStudent}
              addStudent={addStudent}
              removeStudent={removeStudent}
              gradeOptions={GRADES}
            />
          )}
          {step === 3 && (
            <StepAgreement form={form} set={set} />
          )}
          {step === 4 && (
            <StepPayment
              form={form}
              set={set}
              total={total}
              feePerChild={settings?.enrollment_fee_per_child || 1500}
              studentCount={form.students.length}
              partnerNote={settings?.partner_ministry_note}
            />
          )}

          {/* Navigation */}
          <div className="mt-10 flex items-center justify-between border-t border-grey pt-6">
            <button
              type="button"
              onClick={back}
              disabled={step === 1 || submitting}
              className="inline-flex items-center gap-2 text-purple-dark text-sm px-4 py-2 disabled:opacity-30 hover:text-purple transition-colors"
              data-testid="enrollment-back-btn"
            >
              <ChevronLeft size={16} /> Back
            </button>

            {step < 4 ? (
              <button
                type="button"
                onClick={next}
                className="inline-flex items-center gap-2 bg-purple text-white hover:bg-purple-dark font-bold px-8 py-3 rounded-sm transition-colors text-sm"
                data-testid="enrollment-next-btn"
              >
                Continue <ChevronRight size={16} />
              </button>
            ) : (
              <button
                type="button"
                onClick={submit}
                disabled={submitting}
                className="inline-flex items-center gap-2 bg-gold text-ink hover:bg-gold-light font-bold px-8 py-3 rounded-sm transition-colors text-sm disabled:opacity-60"
                data-testid="enrollment-submit-btn"
              >
                {submitting ? "Submitting..." : "Submit Enrollment"}
                {!submitting && <Check size={16} />}
              </button>
            )}
          </div>
        </div>

        <p className="text-xs text-ink/60 text-center mt-6">
          By continuing you agree to the church school covering of Zion Christian
          Academy under Ala. Code § 16-28-1.
        </p>
      </div>

      <SiteFooter />
    </div>
  );
}

/* ---------- STEP COMPONENTS ---------- */
function SectionTitle({ n, title, subtitle }) {
  return (
    <div className="mb-8">
      <div className="text-xs tracking-[0.22em] uppercase text-purple-light font-bold mb-2">
        Step {n}
      </div>
      <h2 className="h-serif text-purple-dark text-3xl tracking-tight mb-2">
        {title}
      </h2>
      {subtitle && (
        <p className="text-ink/70 text-sm leading-relaxed">{subtitle}</p>
      )}
    </div>
  );
}

function Field({ label, children, span = "col-span-1", required }) {
  return (
    <div className={span}>
      <Label className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold mb-2 block">
        {label} {required && <span className="text-gold">*</span>}
      </Label>
      {children}
    </div>
  );
}

function StepFamily({ form, set }) {
  return (
    <div>
      <SectionTitle
        n="1"
        title="Family information"
        subtitle="This is the parent or legal guardian who will be the primary contact for the school."
      />
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="First Name" required>
          <Input
            data-testid="parent-first-name"
            value={form.parent_first_name}
            onChange={(e) => set("parent_first_name", e.target.value)}
            className="border-grey focus:border-purple focus:ring-purple"
          />
        </Field>
        <Field label="Last Name" required>
          <Input
            data-testid="parent-last-name"
            value={form.parent_last_name}
            onChange={(e) => set("parent_last_name", e.target.value)}
            className="border-grey focus:border-purple focus:ring-purple"
          />
        </Field>
        <Field label="Email" required>
          <Input
            type="email"
            data-testid="parent-email"
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            className="border-grey focus:border-purple focus:ring-purple"
          />
        </Field>
        <Field label="Phone" required>
          <Input
            data-testid="parent-phone"
            value={form.phone}
            onChange={(e) => set("phone", e.target.value)}
            className="border-grey focus:border-purple focus:ring-purple"
          />
        </Field>
        <Field label="Street Address" required span="col-span-1 sm:col-span-2">
          <Input
            data-testid="parent-address1"
            value={form.address_line1}
            onChange={(e) => set("address_line1", e.target.value)}
            className="border-grey focus:border-purple focus:ring-purple"
          />
        </Field>
        <Field label="Apt / Unit (optional)" span="col-span-1 sm:col-span-2">
          <Input
            data-testid="parent-address2"
            value={form.address_line2}
            onChange={(e) => set("address_line2", e.target.value)}
            className="border-grey focus:border-purple focus:ring-purple"
          />
        </Field>
        <Field label="City" required>
          <Input
            data-testid="parent-city"
            value={form.city}
            onChange={(e) => set("city", e.target.value)}
            className="border-grey focus:border-purple focus:ring-purple"
          />
        </Field>
        <div className="grid grid-cols-2 gap-5">
          <Field label="State" required>
            <Input
              data-testid="parent-state"
              value={form.state}
              onChange={(e) => set("state", e.target.value.toUpperCase().slice(0, 2))}
              className="border-grey focus:border-purple focus:ring-purple uppercase"
            />
          </Field>
          <Field label="ZIP" required>
            <Input
              data-testid="parent-zip"
              value={form.zip_code}
              onChange={(e) => set("zip_code", e.target.value)}
              className="border-grey focus:border-purple focus:ring-purple"
            />
          </Field>
        </div>
      </div>

      <div className="mt-10 pt-8 border-t border-grey">
        <h3 className="h-serif text-lg text-purple-dark mb-4">Create your portal account</h3>
        <p className="text-sm text-ink/70 mb-5 leading-relaxed">
          You'll use this password to log into your parent portal, download
          documents, and re-enroll next year.
        </p>
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Password" required>
            <Input
              type="password"
              data-testid="parent-password"
              value={form.password}
              onChange={(e) => set("password", e.target.value)}
              className="border-grey focus:border-purple focus:ring-purple"
              placeholder="At least 8 characters"
            />
          </Field>
          <Field label="Confirm Password" required>
            <Input
              type="password"
              data-testid="parent-password-confirm"
              value={form.password_confirm}
              onChange={(e) => set("password_confirm", e.target.value)}
              className="border-grey focus:border-purple focus:ring-purple"
            />
          </Field>
        </div>
      </div>
    </div>
  );
}

function StepStudents({ students, setStudent, addStudent, removeStudent, gradeOptions }) {
  return (
    <div>
      <SectionTitle
        n="2"
        title="Add your students"
        subtitle="You can add as many students as you'd like. You can edit or add students later from your parent portal."
      />
      <div className="space-y-6">
        {students.map((s, i) => (
          <div key={i} className="border border-grey p-5 sm:p-6 bg-grey-light/40" data-testid={`student-row-${i}`}>
            <div className="flex items-center justify-between mb-5">
              <div className="h-serif text-purple-dark text-lg">
                Student {i + 1}
              </div>
              {students.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeStudent(i)}
                  className="text-sm text-red-700 hover:text-red-900 inline-flex items-center gap-1"
                  data-testid={`remove-student-${i}`}
                >
                  <Trash2 size={14} /> Remove
                </button>
              )}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Legal Full Name" required span="col-span-1 sm:col-span-2">
                <Input
                  data-testid={`student-name-${i}`}
                  value={s.full_name}
                  onChange={(e) => setStudent(i, "full_name", e.target.value)}
                  className="border-grey focus:border-purple focus:ring-purple bg-white"
                />
              </Field>
              <Field label="Date of Birth" required>
                <Input
                  type="date"
                  data-testid={`student-dob-${i}`}
                  value={s.date_of_birth}
                  onChange={(e) => setStudent(i, "date_of_birth", e.target.value)}
                  className="border-grey focus:border-purple focus:ring-purple bg-white"
                />
              </Field>
              <Field label="Grade Level" required>
                <Select
                  value={s.grade_level}
                  onValueChange={(v) => setStudent(i, "grade_level", v)}
                >
                  <SelectTrigger
                    className="border-grey focus:border-purple focus:ring-purple bg-white"
                    data-testid={`student-grade-${i}`}
                  >
                    <SelectValue placeholder="Select grade" />
                  </SelectTrigger>
                  <SelectContent>
                    {gradeOptions.map((g) => (
                      <SelectItem key={g} value={g}>
                        {g}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Gender" required span="col-span-1 sm:col-span-2">
                <RadioGroup
                  value={s.gender}
                  onValueChange={(v) => setStudent(i, "gender", v)}
                  className="flex gap-6"
                  data-testid={`student-gender-${i}`}
                >
                  <label className="flex items-center gap-2 cursor-pointer">
                    <RadioGroupItem value="Male" className="border-purple text-purple" />
                    <span className="text-sm text-ink">Male</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <RadioGroupItem value="Female" className="border-purple text-purple" />
                    <span className="text-sm text-ink">Female</span>
                  </label>
                </RadioGroup>
              </Field>
            </div>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={addStudent}
        className="mt-6 inline-flex items-center gap-2 border border-purple text-purple hover:bg-grey-light font-bold px-5 py-2.5 rounded-sm text-sm transition-colors"
        data-testid="add-student-btn"
      >
        <Plus size={14} /> Add another student
      </button>
    </div>
  );
}

function StepAgreement({ form, set }) {
  return (
    <div>
      <SectionTitle
        n="3"
        title="Church school agreement"
        subtitle="Please read carefully and sign below."
      />
      <div className="border border-grey p-6 bg-grey-light/50 max-h-72 overflow-y-auto text-sm text-ink/85 leading-relaxed space-y-3" data-testid="agreement-text">
        <p>
          By enrolling my child(ren) in Zion Christian Academy (an
          independent Christian community school), I understand and agree to
          the following:
        </p>
        <ol className="list-decimal ml-5 space-y-2">
          <li>
            Zion Christian Academy operates as a <strong>church school</strong>{" "}
            under Alabama law (Ala. Code § 16-28-1). Classes are held in-person
            at the fellowship hall of Alta Independent Baptist Church (131
            Willie Williams Rd, Berry, AL 35546), Monday–Thursday, 8:00 AM to
            2:00 PM, with chapel every Wednesday at 1:00 PM.
          </li>
          <li>
            I will notify my local county Board of Education of my child's
            enrollment using the documents provided by ZCA upon confirmation.
          </li>
          <li>
            My student will attend classes consistently and follow the ZCA
            dress code and student handbook as provided in the Welcome Letter.
          </li>
          <li>
            Tuition is due for the school year. I agree to the payment option I
            select on the next screen (cash/check at submission, or a payment
            plan by prior arrangement).
          </li>
          <li>
            My family will conduct itself in a manner consistent with the
            Christian faith and the stated mission of ZCA and Alta Independent
            Baptist Church.
          </li>
          <li>
            All information provided is true and accurate to the best of my
            knowledge.
          </li>
        </ol>
      </div>

      <label className="mt-6 flex items-start gap-3 cursor-pointer">
        <Checkbox
          checked={form.agreement_accepted}
          onCheckedChange={(v) => set("agreement_accepted", !!v)}
          className="mt-0.5 border-purple data-[state=checked]:bg-purple data-[state=checked]:border-purple"
          data-testid="agreement-checkbox"
        />
        <span className="text-sm text-ink leading-relaxed">
          I have read and agree to the church school covering agreement above.
        </span>
      </label>

      <div className="mt-8 pt-6 border-t border-grey">
        <Label className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold mb-2 block">
          Digital Signature (type your full legal name) <span className="text-gold">*</span>
        </Label>
        <Input
          value={form.signature_name}
          onChange={(e) => set("signature_name", e.target.value)}
          className="border-grey focus:border-purple focus:ring-purple h-serif italic text-lg"
          placeholder="e.g., John David Smith"
          data-testid="signature-input"
        />
        <p className="text-xs text-ink/60 mt-2">
          Your typed name serves as your legally binding signature.
        </p>
      </div>
    </div>
  );
}

function StepPayment({ form, set, total, feePerChild, studentCount, partnerNote }) {
  return (
    <div>
      <SectionTitle
        n="4"
        title="How would you like to pay?"
        subtitle="Zion Christian Academy accepts cash or check only — there is no card processing."
      />

      <div className="border border-gold bg-gold-light/20 p-5 mb-8">
        <div className="flex items-baseline justify-between">
          <div>
            <div className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold">
              {studentCount} student{studentCount !== 1 ? "s" : ""} × $
              {feePerChild.toLocaleString()}
            </div>
            <div className="h-serif text-3xl text-purple-dark mt-1">
              ${total.toLocaleString()}
            </div>
          </div>
          <div className="text-xs text-ink/70 text-right max-w-[220px] italic h-serif">
            {partnerNote || "Partner ministry assistance may apply."}
          </div>
        </div>
      </div>

      <RadioGroup
        value={form.payment_method}
        onValueChange={(v) => set("payment_method", v)}
        className="space-y-4"
        data-testid="payment-method-group"
      >
        <label
          className={`flex items-start gap-4 border p-5 cursor-pointer transition-colors ${
            form.payment_method === "cash_check_now"
              ? "border-purple bg-purple/5"
              : "border-grey hover:border-purple-light"
          }`}
          data-testid="payment-option-cash-check"
        >
          <RadioGroupItem
            value="cash_check_now"
            className="mt-1 border-purple text-purple"
          />
          <div>
            <div className="h-serif text-purple-dark text-lg mb-1">
              Cash or check at submission
            </div>
            <div className="text-sm text-ink/80 leading-relaxed">
              After you submit, you'll receive instructions for mailing a check
              or arranging in-person delivery. Your enrollment documents are
              released once payment is received.
            </div>
          </div>
        </label>

        <label
          className={`flex items-start gap-4 border p-5 cursor-pointer transition-colors ${
            form.payment_method === "payment_plan_end_of_year"
              ? "border-purple bg-purple/5"
              : "border-grey hover:border-purple-light"
          }`}
          data-testid="payment-option-plan"
        >
          <RadioGroupItem
            value="payment_plan_end_of_year"
            className="mt-1 border-purple text-purple"
          />
          <div>
            <div className="h-serif text-purple-dark text-lg mb-1">
              Payment plan / pay at end of year
            </div>
            <div className="text-sm text-ink/80 leading-relaxed">
              Working with a specific budget? We're happy to arrange a payment
              plan — including paying at the end of the school year. Our
              director will contact you to finalize the schedule.
            </div>
          </div>
        </label>
      </RadioGroup>
    </div>
  );
}
